import { faArrowUpRightFromSquare } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronUp,
    faPencil,
} from "@fortawesome/sharp-light-svg-icons";
import { Disclosure } from "@headlessui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ArrowPathIcon } from "@heroicons/react/24/solid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { EmissionRecordView } from "../../apiClient/generated";
import {
    useAccountsApiClient,
    useAppSelector,
    useEmissionEventsApiClient,
    useEmissionRecordsApiClient,
} from "../../hooks";
import { ROUTES } from "../../routes";
import { ConfirmationDialog } from "../../ui/ConfirmationDialog";
import { AlignedDataDisplay, CoordinatesField } from "../../ui/GenericFields";
import { FilteringDropdown } from "../../ui/Inputs";
import { formatDateTime } from "../../utils/dates";
import { useCompanyDataForEmission } from "../EventsV2/helpers";
import { UnifiedIcon } from "../UnifiedIcon";

interface EditableUserFieldDisplayProps {
    emissionData?: EmissionRecordView;
    field: "reviewer" | "responsible";
}

export const EditableUserFieldDisplay = (
    props: EditableUserFieldDisplayProps,
) => {
    const apiClient = useAccountsApiClient();
    const eventsApiClient = useEmissionEventsApiClient();
    const queryClient = useQueryClient();

    const userIsApprover = useAppSelector(
        (store) => store.auth.permissions.emissionEventApprove,
    );
    const userHasEditPermissions = useAppSelector(
        (store) => store.auth.permissions.emissionEventEdit,
    );

    const [isEditing, setIsEditing] = useState(false);
    const [selectedUser, setSelectedUser] = useState<number | undefined>(
        props.emissionData?.mainEvent[props.field],
    );
    const [error, setError] = useState<string | undefined>(undefined);

    const usersOnCompany = useQuery({
        queryKey: ["usersForCompany", props.emissionData?.owner, props.field],
        queryFn: async () => {
            return await apiClient.accountsUsersList({
                hasApprovalPermission:
                    props.field === "reviewer" ? true : undefined,
                companymembershipCompany: props.emissionData?.owner,
            });
        },
        enabled: !!props.emissionData?.owner && userIsApprover && isEditing,
    });

    const updateField = useMutation({
        mutationFn: async () => {
            return await eventsApiClient.emissioneventsPartialUpdate({
                id: props.emissionData?.mainEvent.id,
                patchedEmissionEventPartialUpdateRequest: {
                    [props.field]: selectedUser,
                    notifyResponsible: true,
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["emissionRecord", props.emissionData?.id],
            });
            // Also invalidate related emissions list queries
            queryClient.invalidateQueries({
                queryKey: ["emissionsRelatedToEvent"],
            });
            setIsEditing(false);
            setError(undefined);
        },
        onError: () => {
            setSelectedUser(props.emissionData?.mainEvent[props.field]);
            setError("Failed to update field. Please try again.");
        },
    });

    const fieldName =
        props.field === "reviewer" ? "reviewerName" : "responsibleName";
    const displayName = props.emissionData?.mainEvent[fieldName];

    const readonlyStatuses = ["LINKED", "COMPLETED", "REJECTED", "ARCHIVED"];
    const isReadonly =
        readonlyStatuses.includes(
            props.emissionData?.mainEvent.eventStatus ?? "",
        ) || !userHasEditPermissions;

    if (!props.emissionData) return null;

    if (isReadonly || !userIsApprover) {
        return (
            <>{displayName || <span className="italic">Not assigned</span>}</>
        );
    }

    return (
        <>
            <button
                type="button"
                className="rounded hover:bg-slate-200"
                onClick={() => setIsEditing(true)}
            >
                {displayName ? (
                    displayName
                ) : (
                    <span className="italic">Not assigned</span>
                )}
                <FontAwesomeIcon icon={faPencil} className="ml-1" />
            </button>
            {isEditing && (
                <ConfirmationDialog
                    visible={isEditing}
                    onClose={() => {
                        setSelectedUser(
                            props.emissionData?.mainEvent[props.field],
                        );
                        setIsEditing(false);
                        setError(undefined);
                    }}
                    loading={updateField.isPending}
                    onConfirm={() => selectedUser && updateField.mutate()}
                    title={
                        props.field === "responsible"
                            ? "Update responsible"
                            : "Update coordinator"
                    }
                    message={
                        <div>
                            <p>
                                Select the user you want to assign as{" "}
                                {props.field === "responsible"
                                    ? "responsible"
                                    : "coordinator"}{" "}
                                to this emission:
                            </p>
                            <FilteringDropdown
                                value={selectedUser}
                                onChange={(user) =>
                                    setSelectedUser(user as number)
                                }
                                options={(usersOnCompany.data || []).map(
                                    (user) => ({
                                        id: user.id,
                                        displayName: `${user.firstName} ${user.lastName} (${user.email})`,
                                        searchString:
                                            `${user.firstName} ${user.lastName} ${user.email}`.toLowerCase(),
                                    }),
                                )}
                            />

                            {error && (
                                <span className="text-xs text-red-600 font-medium">
                                    {error}
                                </span>
                            )}
                        </div>
                    }
                />
            )}
        </>
    );
};

interface EmissionRecordDisplayProps {
    emissionId?: string;
    emissionRecord?: EmissionRecordView;
    showMatches?: boolean;
}

export const EmissionRecordDisplay = (props: EmissionRecordDisplayProps) => {
    const apiClient = useEmissionRecordsApiClient();
    const [showExtraData, setShowExtraData] = useState(false);

    // Emission information
    const emissionQuery = useQuery({
        queryKey: ["emissionRecord", props.emissionId],
        queryFn: async () => {
            return await apiClient.emissionRecordsRetrieve({
                id: props.emissionId,
            });
        },
        enabled: !!props.emissionId,
        // Cache for 15 seconds
        staleTime: 15000,
    });
    const { company } = useCompanyDataForEmission({
        emissionRecord: emissionQuery.data,
    });

    if (props.emissionId && (emissionQuery.isLoading || !emissionQuery.data)) {
        return (
            <div className="flex w-full justify-center">
                <ArrowPathIcon className="p-1 h-8 animate-spin text-slate-500"></ArrowPathIcon>
            </div>
        );
    }

    const data = emissionQuery.data || props.emissionRecord;
    if (!data) {
        return "Error loading emission data.";
    }

    const viewData = [
        {
            label: "Responsible:",
            value: (
                <EditableUserFieldDisplay
                    emissionData={data}
                    field="responsible"
                />
            ),
        },
        ...(company?.settings.approvalFlowEnabled
            ? [
                  {
                      label: "Coordinator:",
                      value: (
                          <EditableUserFieldDisplay
                              emissionData={data}
                              field="reviewer"
                          />
                      ),
                  },
              ]
            : []),
        {
            label: "Provider name:",
            value: (
                <>
                    {data.providerName}
                    <Link
                        className="ml-1 text-xs underline"
                        to={`${ROUTES.LICENSING}?section_id=${data.providerName}`}
                        target="_blank"
                    >
                        (License)
                    </Link>
                </>
            ),
        },
        props.showMatches
            ? {
                  label:
                      data.mainEvent.eventStatus === "NEW"
                          ? "Site match(es):"
                          : "Site:",
                  value:
                      data.mainEvent.eventStatus === "NEW" ? (
                          <div>
                              {data.siteMatches.map((m, idx) => {
                                  // Compute time ranges within 3 months of the emission
                                  let beforeDate = "";
                                  let afterDate = "";
                                  if (data) {
                                      const timestamp =
                                          data.dataPoint.detectionTimestamp.getTime();
                                      beforeDate = new Date(
                                          timestamp + 45 * 24 * 60 * 60 * 1000,
                                      ).toISOString();
                                      afterDate = new Date(
                                          timestamp - 45 * 24 * 60 * 60 * 1000,
                                      ).toISOString();
                                  }

                                  const params = new URLSearchParams({
                                      site: m.id,
                                      detectionDateRangeAfter: afterDate,
                                      detectionDateRangeBefore: beforeDate,
                                  });
                                  if (data?.id)
                                      params.set("selectedContext", data.id);
                                  const url = `${ROUTES.INFRASTRUCTURE_SITE}?${params.toString()}`;

                                  return (
                                      <a
                                          key={`dataDisplay-${idx}`}
                                          className="flex items-center gap-1"
                                          href={url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                      >
                                          {m.siteName}
                                          <FontAwesomeIcon
                                              icon={faArrowUpRightFromSquare}
                                              className="w-3"
                                          />
                                      </a>
                                  );
                              })}
                          </div>
                      ) : data.site?.id ? (
                          <a
                              className="flex items-center gap-1"
                              href={`${ROUTES.INFRASTRUCTURE_SITE}?site=${data.site.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                          >
                              {data.site.siteName}
                              <FontAwesomeIcon
                                  icon={faArrowUpRightFromSquare}
                                  className="w-3"
                              />
                          </a>
                      ) : (
                          "No associated infrastructure"
                      ),
              }
            : undefined,
        data.dataPoint?.secondaryDataSource
            ? {
                  label: "Secondary data source:",
                  value: data.dataPoint?.secondaryDataSource || "-",
              }
            : undefined,
        data.dataPoint?.data?.quantification_institution
            ? {
                  label: "Quantifying institution:",
                  value: data.dataPoint?.data?.quantification_institution,
              }
            : undefined,
        {
            label: "Coordinates:",
            value: (
                <CoordinatesField
                    coordinates={data.dataPoint.location.coordinates}
                    allowCopy={true}
                    mapLinkParams={{
                        emissionId: data.id,
                        lat: data.dataPoint.location.coordinates[1].toString(),
                        lon: data.dataPoint.location.coordinates[0].toString(),
                    }}
                />
            ),
        },
        {
            label: "Time (UTC):",
            value: formatDateTime(data.dataPoint.detectionTimestamp),
        },
    ].filter((x) => x) as { label: string; value: any }[];

    const extraData = Object.keys(data.dataPoint.data || []).map(
        (key: string) => {
            return {
                label: key.replaceAll("_", " "),
                value: data.dataPoint.data[key],
            };
        },
    );

    if (showExtraData) {
        viewData.push(...[{ label: "---", value: "---" }, ...extraData]);
    }

    if (props.showMatches) {
        viewData.unshift();
    }

    if (
        data.dataPoint.detectedRateUncertainty &&
        data.dataPoint.detectedRateUncertainty > 0
    ) {
        viewData.unshift({
            label: "Detected rate uncertainty:",
            value: (
                <>
                    ±{" "}
                    {(data.dataPoint.detectedRateUncertainty / 1000).toFixed(2)}{" "}
                    kg/h
                </>
            ),
        });
    }

    if (data.dataPoint.detectedRate && data.dataPoint.detectedRate > 0) {
        viewData.unshift({
            label: "Detected rate:",
            value: <>{(data.dataPoint.detectedRate / 1000).toFixed(2)} kg/h</>,
        });
    }

    if (data.dataPoint.concentrationUncertainty) {
        viewData.unshift({
            label: "Concentration uncertainty:",
            value: (
                <>
                    ± {data.dataPoint.concentrationUncertainty?.toFixed(2)}{" "}
                    ppm*m
                </>
            ),
        });
    }

    if (data.dataPoint.concentration) {
        viewData.unshift({
            label: "Concentration:",
            value: <>{data.dataPoint.concentration.toFixed(2)} ppm*m</>,
        });
    }

    return (
        <>
            {data.dataPoint.secondaryDataSource?.includes("Sentinel-5P") && (
                <Disclosure
                    as="div"
                    className="rounded border border-yellow-300 bg-yellow-100 px-2 py-1 text-sm mb-1"
                    defaultOpen
                >
                    {({ open }) => (
                        <>
                            <Disclosure.Button className="flex justify-between w-full items-center gap-1 font-bold text-yellow-700">
                                NOTE: Sentinel-5/TROPOMI
                                <FontAwesomeIcon
                                    icon={open ? faChevronUp : faChevronDown}
                                />
                            </Disclosure.Button>
                            <Disclosure.Panel>
                                <p className="w-full text-yellow-900 mb-1">
                                    Sentinel-5/TROPOMI detections are excluded
                                    from AERSHED analytics due to the likelihood
                                    the plumes are a collection of emissions
                                    from multiple facilities and/or industrial
                                    sources.
                                </p>
                                <p className="w-full text-yellow-900">
                                    The data is provided for informational
                                    purposes only as the published latitude and
                                    longitude is near your facility boundary.
                                </p>
                            </Disclosure.Panel>
                        </>
                    )}
                </Disclosure>
            )}

            <AlignedDataDisplay data={viewData} />
            {extraData.length > 0 && (
                <button
                    className="mt-2 ml-1 text-xs underline"
                    onClick={() => setShowExtraData(!showExtraData)}
                    type="button"
                >
                    Show {showExtraData ? "less" : "more"}
                    <UnifiedIcon
                        icon={showExtraData ? faChevronUp : faChevronDown}
                        className="h-3 w-3 text-black pl-1"
                    />
                </button>
            )}
        </>
    );
};

interface MultipleEmissionRecordDisplayProps {
    emissionRecords?: EmissionRecordView[];
    showMatches?: boolean;
}

export const MultipleEmissionRecordDisplay = (
    props: MultipleEmissionRecordDisplayProps,
) => {
    const [currentEmission, setCurrentEmission] = useState(0);

    // Blank component if no data is passed.
    if (!props.emissionRecords || props.emissionRecords.length === 0) {
        return null;
    }

    return (
        <div>
            {props.emissionRecords.length > 1 && (
                <div className="p-1 mb-1 flex justify-between items-center text-sm">
                    Multiple emissions
                    <div className="flex gap-1 items-center">
                        <button
                            type="button"
                            className="p-2"
                            onClick={(e) => {
                                e.preventDefault();
                                setCurrentEmission(
                                    (currentEmission -
                                        1 +
                                        props.emissionRecords.length) %
                                        props.emissionRecords.length,
                                );
                            }}
                        >
                            <ChevronLeftIcon className="w-5" />
                        </button>
                        {currentEmission + 1}/{props.emissionRecords.length}
                        <button
                            type="button"
                            className="p-2"
                            onClick={(e) => {
                                e.preventDefault();
                                setCurrentEmission(
                                    (currentEmission + 1) %
                                        props.emissionRecords.length,
                                );
                            }}
                        >
                            <ChevronRightIcon className="w-5" />
                        </button>
                    </div>
                </div>
            )}
            <EmissionRecordDisplay
                emissionRecord={props.emissionRecords[currentEmission]}
                showMatches={true}
            />
        </div>
    );
};
